/**
 * @file Every string the host calendar and the get-ready nudge show
 * (host-availability-ui-design §4.3, verbatim).
 *
 * House rules (UI §0): no em dashes, curly apostrophes, and nothing in P2
 * promises that guests can already ask to book (U18). The sentences that
 * would are paired by `BOOKING_REQUESTS_LIVE` (`deckBefore` / `deckLive`,
 * `empty.bodyBefore` / `bodyLive`, `done.close_before` / `done.close`,
 * `BOOKING_READY_COPY.before` / `live`) and the page picks one by lookup.
 *
 * Engineering additions beyond §4.3, each marked `// +` where it sits: the
 * stat pills' words without their number (the number is set large beside
 * them), a closed day's "Booked · DT-…" from BK-13e's `booking_ref`, the
 * agenda's week headings, the "+n more" popover's title, the note counter,
 * the zone refusal under the picker, and the loading line.
 *
 * Plain .js: no JSX, no imports.
 */

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** "3 open, 1 closed": the RangeBar's breakdown and its live sentence share it. */
const breakdownOf = (open, closed) => [open && `${open} open`, closed && `${closed} closed`].filter(Boolean).join(', ');

export const CALENDAR_COPY = {
  documentTitle: 'Calendar | Drip Tours',
  kicker: 'Your calendar',
  headline: 'When guests',
  accent: 'can come.',
  deckLive: 'Every day is open unless you close it. Guests ask for a start date on an open day, and you confirm each request.',
  deckBefore:
    'Every day is open unless you close it. When booking requests open, guests will pick a start date from your open days, and you’ll confirm each request.',
  loading: 'Loading your calendar', // +
  stats: {
    requests: (n) => plural(n, 'request waiting', 'requests waiting'),
    upcoming: (n) => plural(n, 'upcoming booking', 'upcoming bookings'),
    requestsWords: (n) => (n === 1 ? 'request waiting' : 'requests waiting'), // +
    upcomingWords: (n) => (n === 1 ? 'upcoming booking' : 'upcoming bookings'), // +
    tz: (city, offset) => `${city} time · ${offset}`,
    tzChange: 'Change time zone'
  },
  tz: {
    title: 'Confirm your time zone',
    body: 'A start date is a day on your calendar, and a request lapses when its first day begins where you are. Until you confirm, we use UTC, so “today” can be off by a day around midnight.',
    label: 'Your time zone',
    detected: (zone) => `Detected from this browser: ${zone}`,
    save: 'Confirm time zone',
    saving: 'Saving…',
    confirming: 'Confirming…',
    saved: (city) => `Saved. Your calendar runs on ${city} time.`,
    popTitle: 'Time zone',
    popBody: 'Changing it moves “today”, and when waiting requests lapse.',
    popSave: 'Save time zone',
    invalid: 'Choose a time zone from the list.', // + (UI §5.4)
    search: 'Search, e.g. Manila', // +
    nothing: 'No time zone matches that' // +
  },
  ready: {
    title: 'Ready for booking requests',
    tzTodo: 'Confirm your time zone',
    tz: 'Time zone confirmed',
    days: 'Close the days you can’t host (optional)'
  },
  nav: { prev: 'Previous month', next: 'Next month', today: 'Today', loading: (month) => `Loading ${month}` },
  legend: {
    label: 'Key',
    open: 'Open',
    closed: 'Closed',
    window: 'No new starts',
    pending: 'Request',
    confirmed: 'Confirmed',
    completed: 'Completed'
  },
  day: {
    closed: 'Closed',
    notice: 'No new starts',
    horizon: 'Beyond a year',
    more: (n) => `+${n} more`,
    booked: (ref) => `Booked · ${ref}`, // + BK-13e
    moreTitle: (day) => `Bookings on ${day}` // +
  },
  dayAria: ({ long, today, state, bookings, selected }) =>
    [long, today && 'today', state, bookings, selected && 'selected'].filter(Boolean).join(', '),
  dayState: {
    // + the `state` part of dayAria
    past: 'past',
    open: 'open',
    closed: 'closed',
    notice: 'no new starts',
    horizon: 'beyond a year'
  },
  dayBookings: (pending, confirmed) =>
    // + the `bookings` part of dayAria
    [pending && plural(pending, 'request', 'requests'), confirmed && plural(confirmed, 'booking', 'bookings')].filter(Boolean).join(', '),
  barAria: ({ title, guest, range, guests, ref }) => `${title} ${guest}, ${range}, ${guests}, ${ref}.`,
  card: {
    // + the booking card a bar opens; its title, and the bar's label, start with `title[status]`
    title: {
      pending: 'Request from',
      declined: 'Request from',
      withdrawn: 'Request from',
      expired: 'Request from',
      confirmed: 'Booking for',
      cancelled_by_guest: 'Booking for',
      cancelled_by_host: 'Booking for',
      completed: 'Trip with'
    },
    close: 'Close',
    loading: 'Loading the booking',
    openConversation: 'Open conversation',
    openConversationUnread: (n) => `Open conversation · ${n} unread`,
    loadError: {
      title: 'We couldn’t load this booking',
      body: 'Check your connection and try again. The conversation still opens.',
      retry: 'Try again'
    },
    notFound: { title: 'We can’t find this booking any more', body: 'We’ve refreshed your calendar.' }
  },
  barRange: (start, end) => (start === end ? start : `${start} to ${end}`), // +
  guests: (n) => plural(n, 'guest', 'guests'), // +
  gridAria: (month) => `${month}, your days. Arrow keys move. Shift with an arrow selects a range. Escape clears.`,
  dows: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], // +
  dowsLong: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], // +
  select: {
    summary: (range, n) => `${range} · ${plural(n, 'day', 'days')}`,
    breakdown: breakdownOf,
    close: 'Close these dates',
    reopen: 'Reopen these dates',
    closing: 'Closing…',
    reopening: 'Reopening…',
    clear: 'Clear selection',
    addNote: 'Add a private note',
    note: 'Private note',
    noteHint: 'Only you see this. Guests never do.',
    notePlaceholder: 'e.g. Family holiday',
    noteCount: (n, max) => `${n}/${max}`, // +
    region: 'Selected days', // +
    booked: (n) =>
      `${n === 1 ? '1 of these days has a confirmed booking.' : `${n} of these days have confirmed bookings.`} Closing cancels nothing; reopening lets guests request those days again.`,
    waiting: (n) =>
      `${plural(n, 'waiting request overlaps', 'waiting requests overlap')} these days. Closing doesn’t decline them; you still answer each one.`,
    live: (range, n, open, closed) => `Selected ${range}, ${plural(n, 'day', 'days')}: ${breakdownOf(open, closed)}.`,
    cleared: 'Selection cleared.',
    hint: 'Click a day, then another, to select a range. Keyboard: arrows move, Shift with an arrow extends, Escape clears.',
    hintTouch: 'Tap a day, then another, to select a range.'
  },
  done: {
    close: (range, n) => (n ? `Closed ${range}. Guests can’t start a trip on these days now.` : 'Those days were already closed.'),
    // P2: BOOKING_REQUESTS_LIVE false, so nothing about guests.
    close_before: (range, n) => (n ? `Closed ${range}.` : 'Those days were already closed.'),
    reopen: (range, n) => (n ? `Reopened ${range}.` : 'Those days were already open.')
  },
  empty: {
    title: 'Every day is open',
    bodyLive:
      'Guests can ask to start any day from tomorrow to a year ahead. Close the days you can’t host, like holidays or a weekly day off, and they disappear from guests’ date pickers.',
    bodyBefore:
      'Nothing is closed and nothing is booked yet. Close the days you can’t host, like holidays or a weekly day off, and guests won’t be able to pick them once requests open.'
  },
  history: 'Past days are a record: bookings show as they happened, and days can’t be changed.',
  horizon: (date) => `Guests can ask up to a year ahead. Days after ${date} aren’t open as start days yet.`,
  loadError: { title: 'We couldn’t load your calendar', body: 'Check your connection and try again.', retry: 'Try again' },
  writeError: {
    network: 'That didn’t save. Check your connection and try again. Nothing changed.',
    try_again: 'These days just changed in another tab. Try again.',
    past_date: 'Days before today can’t be changed.',
    reason: 'Keep the note to 255 characters.',
    invalid_range: 'Choose at most 366 days at a time.'
  },
  agenda: {
    earlier: 'Show earlier days',
    open: 'Open',
    nothing: 'Open, nothing booked',
    loadMore: (month) => `Show ${month}`,
    loadingMore: 'Loading…',
    dayOf: (i, n) => `day ${i} of ${n}`,
    week: (day) => `Week of ${day}`, // +
    weekIn: (month, day) => `${month} · Week of ${day}`, // + a week heading in a later month
    listAria: (month) => `${month}, your days` // +
  }
};

export const BOOKING_READY_COPY = {
  before: {
    title: 'Booking requests are coming',
    body: (noun) =>
      `Soon ${noun} can ask to book a package for a start date, and you’ll confirm or decline each one here. Take a minute now: confirm your time zone and close the days you can’t host.`,
    action: 'Go to your calendar',
    later: 'Not now'
  },
  live: {
    title: 'Guests can ask to book now',
    body: () => 'Confirm your time zone so their dates line up with your days.',
    action: 'Confirm time zone',
    later: 'Not now'
  },
  navSetup: 'Set up',
  navSetupSr: 'set up needed'
};

export default CALENDAR_COPY;
